import {useDispatch, useSelector} from 'react-redux';
import {getUser, login, LOGIN_FAILURE, signUp, SIGN_UP_FAILURE} from '../actions/UserActions';
import {TimeOffset} from '../../form/helpers/TimeHelper';
import {optionItemFinder} from '../../form/helpers/OptionHelper';
import {
    HTTP_STATUS_ACCESS_DENIED,
    HTTP_STATUS_CODE_EMAIL_SUGGESTION,
    HTTP_STATUS_CODE_FORBIDDEN_EMAIL_DOMAIN,
    HTTP_STATUS_CODE_INVALID_PASSWORD,
    HTTP_STATUS_CODE_USER_DOES_NOT_EXIST,
    HTTP_STATUS_CODE_USER_EXISTS
} from '../../../../common/constants/HttpStatusCode';
import {useEffect, useState} from 'react';
import cookieStorage from '@common/helpers/cookieStorage';
import useInvisibleRecaptcha from './useInvisibleRecaptcha';
import {
    CAPTCHA_CONFIRM_MESSAGE,
    CAPTCHA_ERRORS,
    CAPTCHA_FAILED_MESSAGE,
    CAPTCHA_MAX_ATTEMPTS,
} from '../constants/CaptchaConstants';

function useAuth(successCallback) {
    const dispatch = useDispatch();
    const {user, authorized} = useSelector(state => state.user);
    const {countries} = useSelector(state => state.authOptions.data);
    const [userChecked, setUserChecked] = useState(false);
    const {execute: executeRecaptcha, enabled: recaptchaEnabled} = useInvisibleRecaptcha();

    const getErrorCode = response => {
        const payload = response && response.payload ? response.payload : {};
        const responseBody = payload && payload.response ? payload.response : {};

        if (responseBody && typeof responseBody?.message === 'string') {
            return responseBody.message;
        }

        if (responseBody?.errors && typeof responseBody.errors.detail === 'string') {
            return responseBody.errors.detail;
        }

        return null;
    };

    useEffect(() => {
        const hasAuthKey = cookieStorage.has('_authKey');
        if (!user && hasAuthKey) {
            dispatch(getUser()).finally(() => {
                setUserChecked(true);
            });
        } else {
            setUserChecked(true);
        }
    }, [user]);

    const handleRegister = async (values, recaptchaResponse = '', captchaAttempt = 0) => {
        let nextRecaptchaResponse = recaptchaResponse;
        let nextCaptchaAttempt = captchaAttempt;

        if (!nextRecaptchaResponse && recaptchaEnabled && nextCaptchaAttempt === 0) {
            const recaptchaToken = await executeRecaptcha();
            if (recaptchaToken) {
                nextRecaptchaResponse = recaptchaToken;
                nextCaptchaAttempt = 1;
            }
        }

        const data = {
            email: values.email,
            name: 'Customer',
            phone: optionItemFinder(values.phone.code, countries).code + ' ' + values.phone.number,
            user_time_offset: TimeOffset(),
            subscribe: values.subscribe ? 1 : 0,
            country_iso: optionItemFinder(values.phone.code, countries).iso,
        };

        if (nextRecaptchaResponse) {
            data.recaptchaResponse = nextRecaptchaResponse;
        }

        const response = await dispatch(signUp(data));

        if (response.type === SIGN_UP_FAILURE) {
            const errorCode = getErrorCode(response);
            if (CAPTCHA_ERRORS.includes(errorCode)) {
                if (!recaptchaEnabled || nextCaptchaAttempt >= CAPTCHA_MAX_ATTEMPTS) {
                    return {register: {email: CAPTCHA_CONFIRM_MESSAGE}};
                }

                const recaptchaToken = await executeRecaptcha();
                if (!recaptchaToken) {
                    return {register: {email: CAPTCHA_FAILED_MESSAGE}};
                }

                return handleRegister(values, recaptchaToken, nextCaptchaAttempt + 1);
            }

            const errors = {phone: {}};

            switch (response.payload.status) {
                case HTTP_STATUS_ACCESS_DENIED:
                    errors.email = 'Access is denied!';
                    break;
                case HTTP_STATUS_CODE_USER_EXISTS:
                    errors.email = 'User already exists.';
                    break;
                case HTTP_STATUS_CODE_FORBIDDEN_EMAIL_DOMAIN:
                    errors.email = 'Forbidden email domain.';
                    break;
                case HTTP_STATUS_CODE_EMAIL_SUGGESTION:
                    errors.email = `Did you mean ${response.payload.response.message} ?`;
                    break;
                default:
                    errors.email = 'Try again later.';
            }

            return {register: errors};
        }

        setTimeout(() => successCallback && successCallback(), 0);

        return undefined;
    };

    const handleLogin = async values => {
        const response = await dispatch(login(values));

        if (response.type === LOGIN_FAILURE) {
            const errors = {};

            switch (response.payload.status) {
                case HTTP_STATUS_ACCESS_DENIED:
                    errors.email = 'Access is denied!';
                    break;
                case HTTP_STATUS_CODE_USER_DOES_NOT_EXIST:
                    errors.email = 'User doesn\'t exist.';
                    break;
                case HTTP_STATUS_CODE_INVALID_PASSWORD:
                    errors.password = 'Password is incorrect.';
                    break;
                default:
                    errors.email = 'Try again later.';
            }

            return {login: errors};
        }

        setTimeout(() => successCallback && successCallback(), 0);

        return undefined;
    };

    return values => {
        if (!userChecked) return;

        if (authorized) return;

        if (values.login) return handleLogin(values.login);
        if (values.register) return handleRegister(values.register);
    };
}

export default useAuth;
